/**
 * The subset of screen-config brand-filter flags that decide, in
 * `buildActiveGroupSnapshot`, which market filter (if any) is applied to the
 * brand pool. Threading them in lets this predicate pick the SAME branch the
 * listener would, so a set we keep here is a set the listener will keep too.
 */
export interface BrandSetApplicabilityFlags {
  isBrandsFilteredByReputationMarket?: boolean;
  isBrandsFilteredByOwnReputationMarkets?: boolean;
  isBrandsFilteredByMarket?: boolean;
}

/**
 * Whether a resolved brand-set entity can actually be shown in the current view.
 *
 * This is a faithful mirror of the brand market-filter branch selection in
 * `selectionListener.buildActiveGroupSnapshot` — the two MUST stay in sync, or
 * we either dispatch a set the listener's prune evicts (which re-seeds the first
 * brand, the original bug) or drop a set the listener would have kept (a false
 * "not available in this view" warning). The branches, in the listener's order:
 *
 *  1. `isBrandsFilteredByReputationMarket` (with markets selected) OR
 *     `isBrandsFilteredByOwnReputationMarkets` → the listener keeps a brand only
 *     if its `reputationMarketIds` is non-empty. Adopted brand-set entities are
 *     created with `reputationMarketIds: []` (see adoptBrandSet.ts), so a SET is
 *     always dropped on these screens — genuinely can't render, honest to warn.
 *  2. `isBrandsFilteredByMarket` (with a market selected) → keep iff the set's
 *     `marketIds` includes the selected market.
 *  3. no market filter active → keep any brand in the pool; a SET stays as long
 *     as it exists with markets (do NOT require selected-market membership).
 *
 * Baseline for every branch: the entity must exist and carry at least one market
 * (a DISABLED clone has `marketIds: []` and is dropped everywhere).
 */
export function isBrandSetApplicableInView(
  entity: { marketIds?: number[] } | undefined,
  selectedMarketId: number | undefined,
  flags: BrandSetApplicabilityFlags = {},
): boolean {
  const marketIds = entity?.marketIds ?? [];
  if (marketIds.length === 0) return false;

  // Reputation branches: a brand set has empty reputationMarketIds, so the
  // listener drops it. (Order matches the listener: reputation before market.)
  if (
    flags.isBrandsFilteredByReputationMarket ||
    flags.isBrandsFilteredByOwnReputationMarkets
  ) {
    return false;
  }

  // Market-scoped brand filter: require selected-market membership.
  if (flags.isBrandsFilteredByMarket && selectedMarketId != null) {
    return marketIds.includes(selectedMarketId);
  }

  // No market filter active: the set exists with markets, so it is applicable.
  return true;
}
